import { useEffect, useLayoutEffect, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { Icon, type IconName } from "./Icon.js";
import "./Menu.css";

/**
 * An anchored menu of actions.
 *
 * Six screens had hand-rolled one of these, each with its own idea of how to
 * close. Row actions, bulk actions and overflow all want the same thing:
 * escape closes, a click outside closes, arrows move, and a destructive item
 * looks destructive.
 */

export interface MenuItem {
  id: string;
  label: string;
  icon?: IconName;
  destructive?: boolean;
  disabled?: boolean;
  /** Why an item is disabled. Shown on hover and as the accessible title. */
  title?: string;
  /** A second, quieter line under the label ("Universal — Apple Silicon & Intel"). */
  description?: string;
  onSelect: () => void;
}

export interface MenuProps {
  open: boolean;
  onClose: () => void;
  items: MenuItem[];
  /**
   * A heading over the items, for menus whose labels only make sense together
   * ("Move to" above four column names). Announced as the menu's own name.
   */
  label?: string;
  /**
   * Which corner it grows from, so it never opens off-screen. "side" opens
   * beside the trigger, bottom edges level — for a trigger in a vertical rail,
   * where opening below would cover the rail's own items.
   */
  align?: "start" | "end" | "side";
  children?: ReactNode;
}

function place(trigger: DOMRect, menu: DOMRect, above: boolean, align: MenuProps["align"]) {
  if (align === "side") {
    return { top: Math.max(8, trigger.bottom - menu.height), left: trigger.right + 8 };
  }
  return {
    top: above ? trigger.top - 4 - menu.height : trigger.bottom + 4,
    left: align === "end" ? trigger.right - menu.width : trigger.left,
  };
}

export function Menu({ open, onClose, items, label, align = "end", children }: MenuProps) {
  const ref = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [above, setAbove] = useState(false);
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);

  /*
   * Rendered into document.body rather than beside its trigger.
   *
   * A row menu inside a scrollable table (or any other overflow:auto/hidden
   * ancestor — a card, a sheet, a modal) used to be clipped the moment it
   * would open past that ancestor's edge: visually cut off or invisible
   * entirely, even though `above`/`align` had already tried to keep it
   * on-screen. Escaping to the body sidesteps every such ancestor at once.
   * Position is computed from the trigger's own viewport rect instead of
   * relying on CSS `position: absolute` within a `position: relative`
   * wrapper, since a portaled node has no such parent to be relative to.
   */
  useLayoutEffect(() => {
    if (!open) {
      setAbove(false);
      setPosition(null);
      return;
    }
    const trigger = ref.current;
    const node = menuRef.current;
    if (!trigger || !node) return;

    const triggerRect = trigger.getBoundingClientRect();
    const menuRect = node.getBoundingClientRect();
    const flip = align !== "side" && triggerRect.bottom + 4 + menuRect.height > window.innerHeight - 8;
    setAbove(flip);
    setPosition(place(triggerRect, menuRect, flip, align));
  }, [open, above, align]);

  // A portaled menu no longer scrolls with whatever ancestor moved under it —
  // without this it stays pinned to the old spot while its trigger slides
  // away underneath.
  useEffect(() => {
    if (!open) return;
    const reposition = () => {
      const trigger = ref.current;
      const node = menuRef.current;
      if (!trigger || !node) return;
      const triggerRect = trigger.getBoundingClientRect();
      const menuRect = node.getBoundingClientRect();
      setPosition(place(triggerRect, menuRect, above, align));
    };
    window.addEventListener("scroll", reposition, true);
    window.addEventListener("resize", reposition);
    return () => {
      window.removeEventListener("scroll", reposition, true);
      window.removeEventListener("resize", reposition);
    };
  }, [open, above, align]);

  /*
   * The trigger is somebody else's element — it arrives as `children`, so we
   * cannot put `aria-expanded` on it in JSX. Stamping it here keeps the
   * menu-button pattern intact without every call site having to remember, and
   * without Menu dictating what the trigger is allowed to be.
   */
  useEffect(() => {
    const trigger = ref.current?.querySelector<HTMLElement>(":scope > button, :scope > [role='button']");
    if (!trigger) return;
    trigger.setAttribute("aria-haspopup", "menu");
    trigger.setAttribute("aria-expanded", String(open));
  }, [open]);

  // Focus moves into the menu on open: a menu you can see but not reach with
  // the keyboard is a mouse-only control wearing menu markup.
  useEffect(() => {
    if (!open) return;
    menuRef.current?.querySelector<HTMLButtonElement>("[role='menuitem']:not(:disabled)")?.focus();
  }, [open]);

  useEffect(() => {
    if (!open) return;

    // Pointer down rather than click: a click listener fires after the button
    // that opened the menu, which would close it again immediately.
    // Checked against both the trigger wrapper and the portaled menu — the
    // menu is no longer a DOM descendant of `ref` once it renders into
    // document.body, so `ref.current.contains(...)` alone would treat every
    // click on a menu item as a click outside and close the menu before its
    // own onClick ever ran.
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node;
      if (ref.current?.contains(target) || menuRef.current?.contains(target)) return;
      onClose();
    };
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, [open, onClose]);

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Escape") {
      event.preventDefault();
      onClose();
      // Back to the trigger, not to the top of the document. Escaping a menu
      // should leave the keyboard where it was before the menu opened.
      ref.current?.querySelector<HTMLElement>(":scope > button, :scope > [role='button']")?.focus();
      return;
    }
    if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return;

    event.preventDefault();
    // menuRef, not ref: the menu is portaled out of ref's DOM subtree, so a
    // DOM query (unlike React event bubbling) has to target it directly.
    const buttons = [...(menuRef.current?.querySelectorAll<HTMLButtonElement>("[role='menuitem']:not(:disabled)") ?? [])];
    const index = buttons.indexOf(document.activeElement as HTMLButtonElement);
    const next =
      event.key === "ArrowDown"
        ? (index + 1) % buttons.length
        : (index - 1 + buttons.length) % buttons.length;
    buttons[next]?.focus();
  };

  return (
    <div className="ui-menu-wrap" ref={ref} onKeyDown={onKeyDown}>
      {children}
      {open &&
        createPortal(
          <div
            className={`ui-menu ${above ? "is-above" : ""}`}
            role="menu"
            aria-label={label}
            ref={menuRef}
            style={{
              position: "fixed",
              // Held off-screen until the first measurement pass has a real
              // trigger/menu rect to compute from — visible with the wrong
              // coordinates for one frame reads as a flicker in the corner.
              top: position ? position.top : -9999,
              left: position ? position.left : -9999,
              visibility: position ? "visible" : "hidden",
            }}
          >
            {label && <div className="ui-menu__label">{label}</div>}
            {items.map((item) => (
              <button
                key={item.id}
                type="button"
                role="menuitem"
                disabled={item.disabled}
                title={item.title}
                className={`ui-menu__item ${item.destructive ? "is-destructive" : ""} ${item.description ? "has-desc" : ""}`}
                onClick={() => {
                  item.onSelect();
                  onClose();
                }}
              >
                {item.icon && <Icon name={item.icon} size={item.description ? 16 : 14} />}
                {item.description ? (
                  <span className="ui-menu__text">
                    <span>{item.label}</span>
                    <span className="ui-menu__desc">{item.description}</span>
                  </span>
                ) : (
                  item.label
                )}
              </button>
            ))}
          </div>,
          document.body,
        )}
    </div>
  );
}
